import { useMemo, useState } from "react";

import { PickerView } from "./PickerView";
import { localeCompareBy } from "./sortUtils";
import type { PickerItem, PickerProps } from "./types";

export type { PickerItem, PickerProps } from "./types";

// `.jsx` callers aren't type-checked, so a null/undefined `label` can reach here at runtime.
const byLabel = localeCompareBy<PickerItem>((item) => item.label ?? "");

export const Picker = ({
  items,
  value,
  onChange,
  onCreateItem,
  placeholder = "Select...",
  searchPlaceholder = "Search...",
  displayLabel = null,
  isLoading = false,
  groups = false,
  groupOrder,
  disabled = false,
  commandEmptyLabel = "No results found.",
  triggerChevronVariant = "chevrons",
  /** Default false so the list is usable nested in menus/dialogs without body scroll-lock / pointer-events issues. */
  popoverModal = false,
  trigger,
  contentClassName,
  sortItems = false,
  triggerVariant = "default",
  holdOpen = false,
}: PickerProps) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  /** Remount Command when closing so cmdk clears the internal input (uncontrolled). */
  const [commandResetNonce, setCommandResetNonce] = useState(0);

  const selectedLabel = items.find((item) => item.value === value)?.label;
  const triggerText = displayLabel ?? selectedLabel ?? placeholder;

  const sortedItems = useMemo(
    () => (sortItems ? [...items].sort(byLabel) : items),
    [items, sortItems]
  );

  const filteredItems = useMemo(
    () =>
      sortedItems.filter((item) => (item.label ?? "").toLowerCase().includes(query.toLowerCase())),
    [sortedItems, query]
  );

  const normalizedQuery = query.trim().toLowerCase();
  const hasExactLabelMatch = useMemo(
    () =>
      normalizedQuery.length > 0 &&
      items.some((item) => (item.label ?? "").trim().toLowerCase() === normalizedQuery),
    [items, normalizedQuery]
  );

  /** Offer create when there is no firm/item whose label exactly matches the search (GitHub-style). */
  const showCreate = !!onCreateItem && normalizedQuery.length > 0 && !hasExactLabelMatch;
  const createLabel = query.trim();

  const resetCommandState = () => {
    setQuery("");
    setCommandResetNonce((n) => n + 1);
  };

  const handleOpenChange = (nextOpen: boolean) => {
    if (!nextOpen && holdOpen) return;
    setOpen(nextOpen);
    if (!nextOpen) {
      resetCommandState();
    }
  };

  const handleSelect = (itemValue: string) => {
    onChange(itemValue);
    handleOpenChange(false);
  };

  const handleCreate = async () => {
    if (!onCreateItem || !createLabel) return;
    try {
      const result = await onCreateItem(createLabel);
      if (result !== false) {
        handleOpenChange(false);
      }
    } catch {
      // Leave popover open so user can retry; error surfacing is the caller's responsibility
    }
  };

  return (
    <PickerView
      filteredItems={filteredItems}
      value={value}
      open={open}
      triggerText={triggerText}
      isLoading={isLoading}
      disabled={disabled}
      groups={groups}
      groupOrder={groupOrder}
      commandEmptyLabel={commandEmptyLabel}
      searchPlaceholder={searchPlaceholder}
      triggerChevronVariant={triggerChevronVariant}
      popoverModal={popoverModal}
      trigger={trigger}
      contentClassName={contentClassName}
      triggerVariant={triggerVariant}
      showCreate={showCreate}
      createLabel={createLabel}
      commandResetNonce={commandResetNonce}
      holdOpen={holdOpen}
      onOpenChange={handleOpenChange}
      onQueryChange={setQuery}
      onSelect={handleSelect}
      onCreate={handleCreate}
    />
  );
};
